import React, { useState, useEffect, useMemo } from 'react';
import { UIUser } from 'types';
import { useUserContext } from 'src/context/UserProvider';
import { Modal, Button, Label } from 'flowbite-react';
import { useSearchParams } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { useGetEligibleDrives } from 'src/hooks/offlineDrive.hooks';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import { useQuery } from 'react-query';
import { registerForDrive } from 'src/apis/offlineDrive';
import { baseApi } from 'src/apis/axios';


interface Props {
    user?: UIUser;
    openEditDriveLabelModal: boolean;
    setOpenEditDriveLabelModal: React.Dispatch<React.SetStateAction<boolean>>;
    onSuccess?: () => void;
}

const EditDriveLabelModal = ({
    user,
    openEditDriveLabelModal,
    setOpenEditDriveLabelModal,
    onSuccess,
}: Props) => {
    const { selectedDrive } = useUserContext();
    const [searchParams] = useSearchParams();
    const driveId = searchParams.get('driveId') || selectedDrive?.value;
    const [selectedDriveLabel, setSelectedDriveLabel] = useState<{ label: string; value: string } | null>(null);

    // Fetch eligible drives to get drive details
    const { data: eligibleDrivesData } = useGetEligibleDrives({ driveId: driveId || undefined });

    // Fetch placement drive labels from dropdown menu
    const { data: placementDriveData } = useGetDropdownItemsByCategory('PLACEMENT_DRIVE', undefined, true);

    // Fetch drive directly by ID to ensure we get all driveLabel values
    const { data: driveData } = useQuery(
        ['getDriveById', driveId],
        async () => {
            if (!driveId) return null;
            try {
                // Use getAllDrivesData API which accepts id parameter and returns full drive data
                const response = await baseApi.get(`drive/getAllDrivesData`, { params: { id: driveId } });
                const drive = response.data?.data?.[0] || null;
                console.log('EditDriveLabelModal - Directly fetched drive:', drive);
                console.log('EditDriveLabelModal - Directly fetched driveLabel:', drive?.driveLabel);
                console.log('EditDriveLabelModal - driveLabel type:', typeof drive?.driveLabel, Array.isArray(drive?.driveLabel));
                return drive;
            } catch (error) {
                console.error('Error fetching drive:', error);
                return null;
            }
        },
        {
            enabled: !!driveId && openEditDriveLabelModal,
        }
    );

    // Fetch existing DriveInvitee to get current driveLabel
    const { data: existingDriveInvitee, refetch } = useQuery(
        ['getDriveInvitee', user?._id, driveId],
        async () => {
            if (!user?._id || !driveId) return null;
            const response = await baseApi.get(`offline-drive/getDriveInvitee?userId=${user._id}&driveId=${driveId}`);
            return response.data;
        },
        {
            enabled: !!user?._id && !!driveId && openEditDriveLabelModal,
        }
    );

    // Get the selected drive details - prefer direct fetch, fallback to eligibleDrives
    const selectedDriveDetails = useMemo(() => {
        if (!driveId) return null;
        // First try to use directly fetched drive data (more reliable)
        if (driveData) {
            console.log('EditDriveLabelModal - Using directly fetched driveData:', driveData);
            console.log('EditDriveLabelModal - driveData.driveLabel:', driveData.driveLabel);
            return driveData;
        }
        // Fallback to eligibleDrives data
        if (eligibleDrivesData?.eligibleDrives) {
            const drive = eligibleDrivesData.eligibleDrives.find(drive => drive._id === driveId);
            console.log('EditDriveLabelModal - Using eligibleDrives data:', drive);
            console.log('EditDriveLabelModal - eligibleDrives driveLabel:', drive?.driveLabel);
            return drive;
        }
        return null;
    }, [driveId, driveData, eligibleDrivesData]);

    // Get drive label options based on selected drive's driveLabel field
    const driveLabelOptions = useMemo(() => {
        console.log('EditDriveLabelModal - selectedDriveDetails:', selectedDriveDetails);
        console.log('EditDriveLabelModal - selectedDriveDetails.driveLabel:', selectedDriveDetails?.driveLabel);

        if (!selectedDriveDetails?.driveLabel) {
            console.log('EditDriveLabelModal - No driveLabel found in selectedDriveDetails');
            return [];
        }

        const driveLabels: string[] = Array.isArray(selectedDriveDetails.driveLabel)
            ? selectedDriveDetails.driveLabel
            : [String(selectedDriveDetails.driveLabel)];

        console.log('EditDriveLabelModal - driveLabels array:', driveLabels);

        if (driveLabels.length === 0) {
            console.log('EditDriveLabelModal - driveLabels array is empty');
            return [];
        }

        // If placement drive data is available, try to match with dropdown menu items
        if (placementDriveData?.lmsTechStacks && placementDriveData.lmsTechStacks.length > 0) {
            const placementDriveMap = new Map<string, string>();
            placementDriveData.lmsTechStacks
                .filter(item => item.isActive)
                .forEach(item => {
                    const itemLabel = item.label || item.name;
                    placementDriveMap.set(itemLabel, itemLabel);
                    if (item.name && item.name !== itemLabel) {
                        placementDriveMap.set(item.name, itemLabel);
                    }
                });

            const options = driveLabels
                .map(driveLabel => {
                    const matchedLabel = placementDriveMap.get(driveLabel) || driveLabel;
                    return {
                        label: matchedLabel,
                        value: matchedLabel,
                    };
                })
                .filter((option, index, self) => {
                    // Remove duplicates based on label only
                    const firstIndex = self.findIndex(o => o.label === option.label);
                    return index === firstIndex;
                });

            console.log('EditDriveLabelModal - Final options with placement data:', options);
            return options;
        }

        // Fallback: show labels directly
        const fallbackOptions = driveLabels.map(label => ({
            label: label,
            value: label,
        }));
        console.log('EditDriveLabelModal - Fallback options:', fallbackOptions);
        return fallbackOptions;
    }, [selectedDriveDetails, placementDriveData]);

    // Set the current driveLabel when existing data is loaded and options are available
    useEffect(() => {
        if (existingDriveInvitee?.driveInvitee?.driveLabel && driveLabelOptions.length > 0) {
            const currentLabel = existingDriveInvitee.driveInvitee.driveLabel;
            console.log('EditDriveLabelModal - Setting current label:', currentLabel);
            console.log('EditDriveLabelModal - Available options:', driveLabelOptions);

            // Try to find exact match first
            let matchingOption = driveLabelOptions.find(opt =>
                opt.value === currentLabel || opt.label === currentLabel
            );

            // If no exact match, try case-insensitive match
            if (!matchingOption) {
                matchingOption = driveLabelOptions.find(opt =>
                    opt.value.toLowerCase() === currentLabel.toLowerCase() ||
                    opt.label.toLowerCase() === currentLabel.toLowerCase()
                );
            }

            if (matchingOption) {
                console.log('EditDriveLabelModal - Found matching option:', matchingOption);
                setSelectedDriveLabel(matchingOption);
            } else {
                // If current label is not in options, create a custom option
                console.log('EditDriveLabelModal - No matching option found, creating custom option');
                setSelectedDriveLabel({ label: currentLabel, value: currentLabel });
            }
        } else if (!existingDriveInvitee?.driveInvitee?.driveLabel && driveLabelOptions.length > 0) {
            // If no existing label but options are available, don't set anything (let user choose)
            setSelectedDriveLabel(null);
        }
    }, [existingDriveInvitee, driveLabelOptions]);

    // Reset when modal opens/closes
    useEffect(() => {
        if (openEditDriveLabelModal) {
            refetch();
            // Reset selected label when modal opens - it will be set by the other useEffect when data loads
            setSelectedDriveLabel(null);
        } else {
            setSelectedDriveLabel(null);
        }
    }, [openEditDriveLabelModal, refetch]);

    const handleUpdate = async () => {
        if (!user?._id || !driveId) return;

        try {
            await baseApi.post('offline-drive/registerForDrive', {
                user: user._id,
                driveId: driveId,
                remarks: 'Drive label updated by admin',
                driveLabel: selectedDriveLabel?.value || undefined,
            });

            if (onSuccess) {
                onSuccess();
            }
            setOpenEditDriveLabelModal(false);
        } catch (error: any) {
            console.error('Error updating drive label:', error);
        }
    };

    return (
        <Modal show={openEditDriveLabelModal} onClose={() => setOpenEditDriveLabelModal(false)}>
            <Modal.Header className='font-semibold text-[12px]'>
                Edit Drive Label
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <p className="text-sm text-gray-600">
                        <strong>Name:</strong> {user?.name}
                    </p>
                    <p className="text-sm text-gray-600">
                        <strong>Email:</strong> {user?.email}
                    </p>
                    {existingDriveInvitee?.driveInvitee?.driveLabel && (
                        <p className="text-sm text-gray-600 mt-2">
                            <strong>Current Drive Label:</strong> {existingDriveInvitee.driveInvitee.driveLabel}
                        </p>
                    )}
                </div>
                {driveId && driveLabelOptions.length > 0 ? (
                    <div className="mt-4">
                        <Label htmlFor="driveLabel" value="Drive Label (Placement Drive)" />
                        <Select
                            menuPlacement={'auto'}
                            classNamePrefix="my-select-plans"
                            closeMenuOnSelect={true}
                            placeholder="Select drive label"
                            options={driveLabelOptions}
                            value={selectedDriveLabel}
                            onChange={(value: SingleValue<{ label: string; value: string }>) => {
                                setSelectedDriveLabel(value);
                            }}
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Select or change the drive label for this user's registration.
                        </p>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 mt-2">
                        {!driveId
                            ? 'Please select a drive first.'
                            : 'This drive does not have any drive labels configured.'}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer className='flex justify-end gap-4'>
                <Button color="gray" onClick={() => setOpenEditDriveLabelModal(false)}>
                    Cancel
                </Button>
                {driveId && driveLabelOptions.length > 0 && (
                    <Button onClick={handleUpdate}>
                        Update Drive Label
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default EditDriveLabelModal;

